import React, { useState } from 'react';
import {
    Truck, Search, Filter, Plus,
    AlertCircle, CheckCircle,
    Clock, ExternalLink, ShieldCheck,
    MessageSquare, Paperclip, ChevronRight, Zap
} from 'lucide-react';

const SupplierPortal: React.FC = () => {
    const [searchQuery, setSearchQuery] = useState('');

    const supplierActions = [
        {
            id: 'ACT-908',
            supplier: 'Bosch Automotive',
            issue: 'Sensor drift reported in Pump #429',
            linkType: '8D (D2 - Containment)',
            linkId: '8D-2026-003',
            status: 'Pending Evidence',
            dueDate: '2026-02-15',
            priority: 'Critical',
        },
        {
            id: 'ACT-912',
            supplier: 'TE Connectivity',
            issue: 'Micro-fracture in harness housing',
            linkType: 'RCA (Ishikawa)',
            linkId: 'RCA-128',
            status: 'Reviewing',
            dueDate: '2026-02-22',
            priority: 'High',
        },
        {
            id: 'ACT-915',
            supplier: 'NXP Semiconductors',
            issue: 'Firmware bug in revision B',
            linkType: 'FRACAS',
            linkId: 'FRAC-8822',
            status: 'Resolved',
            dueDate: '2026-01-30',
            priority: 'Medium',
        }
    ];

    const stats = [
        { label: 'Active Actions', value: 12, icon: AlertCircle, color: 'text-amber-600', bg: 'bg-amber-100' },
        { label: 'Late Responses', value: 2, icon: Clock, color: 'text-red-600', bg: 'bg-red-100' },
        { label: 'Accepted Evidence', value: 85, icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-100' },
        { label: 'Compliance Score', value: '94%', icon: ShieldCheck, color: 'text-blue-600', bg: 'bg-blue-100' },
    ];

    return (
        <div className="p-8 max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-8">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                            <Truck className="w-8 h-8 text-blue-600" />
                            Supplier Action Portal
                        </h1>
                        <p className="text-gray-600 mt-1">
                            Track external root cause evidence and containment actions from supply chain partners
                        </p>
                    </div>
                    <div className="flex gap-3">
                        <button className="flex items-center gap-2 px-4 py-2 border border-blue-200 bg-blue-50 text-blue-600 rounded-lg font-bold hover:bg-blue-100 transition-colors">
                            <Zap className="w-4 h-4" />
                            Magnus AI Audit
                        </button>
                        <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                            <Plus className="w-4 h-4" />
                            New Action Request
                        </button>
                    </div>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                {stats.map((s, i) => (
                    <div key={i} className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                        <div className="flex items-center gap-4">
                            <div className={`p - 3 rounded - lg ${s.bg} `}>
                                <s.icon className={`w - 6 h - 6 ${s.color} `} />
                            </div>
                            <div>
                                <p className="text-sm text-gray-500 font-medium">{s.label}</p>
                                <p className="text-2xl font-bold text-gray-900">{s.value}</p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Filters & Search */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-6 flex flex-wrap items-center justify-between gap-4">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search by supplier or action ID..."
                        className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
                <div className="flex gap-2">
                    <button className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600 transition-colors">
                        <Filter className="w-4 h-4" />
                        Type: All
                    </button>
                    <button className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600 transition-colors">
                        Status: Active
                    </button>
                </div>
            </div>

            {/* Actions Table */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <table className="w-full text-left">
                    <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                            <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-widest">Action / ID</th>
                            <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-widest">Supplier</th>
                            <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-widest">Linked To</th>
                            <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-widest">Status</th>
                            <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-widest">Due Date</th>
                            <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-widest">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {supplierActions.map((action) => (
                            <tr key={action.id} className="hover:bg-slate-50 transition-colors group">
                                <td className="px-6 py-4">
                                    <div className="font-bold text-gray-900">{action.id}</div>
                                    <div className="text-xs text-gray-500 mt-1 line-clamp-1">{action.issue}</div>
                                </td>
                                <td className="px-6 py-4">
                                    <div className="text-sm font-medium text-gray-700">{action.supplier}</div>
                                </td>
                                <td className="px-6 py-4">
                                    <div className="flex items-center gap-2">
                                        <span className="text-[10px] bg-indigo-50 text-indigo-700 font-bold px-1.5 py-0.5 rounded">
                                            {action.linkType}
                                        </span>
                                        <span className="text-xs font-mono text-gray-500">{action.linkId}</span>
                                    </div>
                                </td>
                                <td className="px-6 py-4">
                                    <span className={`px - 2 py - 1 rounded - full text - [10px] font - bold uppercase ${action.status === 'Resolved' ? 'bg-green-100 text-green-700' :
                                            action.status === 'Pending Evidence' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                                        } `}>
                                        {action.status}
                                    </span>
                                </td>
                                <td className="px-6 py-4">
                                    <div className={`text - sm ${action.priority === 'Critical' ? 'text-red-600 font-bold underline' : 'text-gray-600'} `}>
                                        {action.dueDate}
                                    </div>
                                </td>
                                <td className="px-6 py-4">
                                    <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button className="p-2 hover:bg-white rounded-lg border border-transparent hover:border-gray-200 transition-all text-blue-600" title="Reply/Message">
                                            <MessageSquare className="w-4 h-4" />
                                        </button>
                                        <button className="p-2 hover:bg-white rounded-lg border border-transparent hover:border-gray-200 transition-all text-gray-600" title="Attachments">
                                            <Paperclip className="w-4 h-4" />
                                        </button>
                                        <button className="flex items-center gap-1 px-3 py-1 bg-white border border-gray-200 rounded-lg text-xs font-bold text-gray-700 hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-all">
                                            VIEW <ChevronRight className="w-3 h-3" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Quick Links / Guide */}
            <div className="grid md:grid-cols-2 gap-8 mt-8">
                <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-8 text-white">
                    <h3 className="text-xl font-bold mb-4 flex items-center gap-3">
                        <ShieldCheck className="w-6 h-6 text-emerald-400" />
                        Integrated Compliance
                    </h3>
                    <p className="text-slate-300 text-sm mb-6 leading-relaxed">
                        The Supplier Action Portal automatically links responses to AIAG-VDA 8D reports
                        and FRACAS records, ensuring IATF 16949 compliance for external non-conformances.
                    </p>
                    <button className="px-6 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-all text-sm font-semibold border border-white/20 flex items-center gap-2">
                        View Compliance Rules <ExternalLink className="w-4 h-4" />
                    </button>
                </div>

                <div className="bg-blue-50 border border-blue-100 rounded-2xl p-8">
                    <h3 className="text-xl font-bold text-blue-900 mb-4 flex items-center gap-3">
                        <Clock className="w-6 h-6 text-blue-600" />
                        Supplier Response SLAs
                    </h3>
                    <div className="space-y-4">
                        <div className="flex justify-between items-center text-sm">
                            <span className="text-blue-700 font-medium">Initial Containment (D3)</span>
                            <span className="bg-blue-600 text-white px-2 py-0.5 rounded font-bold">24 HOURS</span>
                        </div>
                        <div className="flex justify-between items-center text-sm border-t border-blue-100 pt-4">
                            <span className="text-blue-700 font-medium">Root Cause Evidence (D4)</span>
                            <span className="bg-blue-600 text-white px-2 py-0.5 rounded font-bold">5 DAYS</span>
                        </div>
                        <div className="flex justify-between items-center text-sm border-t border-blue-100 pt-4">
                            <span className="text-blue-700 font-medium">Full Corrective Action (D7)</span>
                            <span className="bg-blue-600 text-white px-2 py-0.5 rounded font-bold">14 DAYS</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SupplierPortal;
